import {
  Component,
  computed,
  EventEmitter,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subject, merge } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { TAutoNumericOptions } from 'src/app/core/helpers/create-autonumeric';

import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { EIconPath } from '../../../shared/enums/icons.enum';
import { TCurrencyCode, TCurrencyData } from '../currencies/currencies.consts';
import { ECustomFieldType } from '../custom-field-types-enums';
import { getIsContainsSlash } from '../custom-fields-service.utils';
import { TCustomFieldTypeData, customFieldTypesData } from '../custom-fields.data';
import { TCustomField } from '../custom-fields.model';
import { CustomFieldsService, ECustomFieldsEventType } from '../custom-fields.service';
import AddToGroup from 'react-lib/components/molecules/AddToGroup';
import {
  TGroupConfig,
  TLayoutConfig,
} from 'react-lib/components/organisms/CustomFieldGroupsSettings';

@Component({
  selector: 'pp-edit-custom-field-details',
  templateUrl: './edit-custom-field-details.component.html',
  styleUrls: [
    './edit-custom-field-details.component.scss',
    '../add-custom-field-modal/add-custom-field-modal.component.scss',
  ],
  standalone: false,
})
export class EditCustomFieldDetailsComponent implements OnChanges, OnInit, OnDestroy {
  ppCustomField = input<TCustomField>();
  @Input() ppWorkspaceId: string;
  @Input() ppEdit = false;
  ppCustomFieldGroupOptions = input<TLayoutConfig[]>([]);

  @Output() ppCustomFieldChange = new EventEmitter();
  @Output() ppListError = new EventEmitter();
  @Output() ppGoBack = new EventEmitter();

  private readonly destroy$ = new Subject<void>();

  customFieldDisplayData: TCustomFieldTypeData;

  customFieldTypes = ECustomFieldType;
  customFieldTypesData = customFieldTypesData;
  focused = false;
  currency: TCurrencyCode;
  unit: string = null;
  EIconPath = EIconPath;
  currencies: TCurrencyData[] = [];
  formula: string;
  subFieldToggleLabel = '';

  sorted = this.customFieldsService.getAllSorted();
  maxListDepthExceeded = false;
  initialCheckboxForSubfields = false;
  displaySubfieldWarning = false;
  customFieldGroupsEnabled = false;
  AddToGroup = AddToGroup;
  AddToGroupProps = computed(() => ({
    options: this.ppCustomFieldGroupOptions().filter(
      (option): option is TGroupConfig => option.type === 'GROUP',
    ),
    selectedGroupId: this.ppCustomField()?.groupId,
    onSelect: this.handleSelectCustomFieldGroup.bind(this),
  }));

  autonumericOptions: TAutoNumericOptions = {
    modifyValueOnWheel: true,
    minimumValue: '0',
    maximumValue: '4',
    allowDecimalPadding: false,
    decimalPlaces: 0,
    decimalPlacesRawValue: 0,
    formulaMode: true,
    watchExternalChanges: true,
  };

  private listDepth = 0;
  private maxListDepth = 0;

  constructor(
    private customFieldsService: CustomFieldsService,
    private translationPipe: TranslationPipe,
    private featureFlagsService: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    merge(
      this.customFieldsService.depthChange$.pipe(
        tap(() => {
          this.listDepth = this.customFieldsService.getCustomFieldListDepth(
            this.ppCustomField().subList || [],
          );
        }),
      ),
      this.customFieldsService.maxListDepthChange$.pipe(
        tap((depth) => {
          this.maxListDepth = depth;
        }),
      ),
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkDepthExceeded();
      });

    this.setSubValuesLabel();

    this.initialCheckboxForSubfields = this.ppCustomField().subValuesActive;
    this.checkFeatureFlag();
  }

  ngOnChanges(): void {
    const customField = this.ppCustomField();

    this.customFieldDisplayData = this.customFieldTypesData.find(
      (cf) => cf.value === customField.type,
    );

    this.currency = customField.currencyCode;

    if (
      (customField.type === ECustomFieldType.NUMBERS ||
        customField.type === ECustomFieldType.PERCENTAGE) &&
      !customField.decimalPlaces &&
      customField.decimalPlaces !== 0
    ) {
      this.updateCustomField({
        ...customField,
        decimalPlaces: 0,
      });
    }

    if (customField.type === ECustomFieldType.FORMULA) {
      this.formula = customField.formula;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  handleSelectCustomFieldGroup(groupId: string): void {
    this.updateCustomField({
      ...this.ppCustomField(),
      groupId,
    });
  }

  setSubValuesLabel(): void {
    let subFieldToggleLabelType = '';

    switch (this.ppCustomField().type) {
      case ECustomFieldType.COST:
        subFieldToggleLabelType = 'Cost';
        break;
      case (ECustomFieldType.PERCENTAGE, ECustomFieldType.NUMBERS):
        subFieldToggleLabelType = 'Numeric';
        break;
    }

    this.subFieldToggleLabel = this.translationPipe.transform('sub_field_toggle_label', {
      customFieldType: subFieldToggleLabelType,
    });
  }

  clearName(): void {
    this.updateCustomField({
      ...this.ppCustomField(),
      label: '',
    });
  }

  onInputFocus(): void {
    this.focused = true;
  }

  onInputFocusLost(): void {
    this.focused = false;
  }

  alphabetizeListEntries(): void {
    this.customFieldsService.emit(ECustomFieldsEventType.ALPHABETIZE, this.sorted.sorted);

    this.customFieldsService.setAllSorted(true);
    this.customFieldsService.emit(ECustomFieldsEventType.ALL_SORT);
  }

  blurInput(event: Event): void {
    (event.target as HTMLElement).blur();
  }

  updateValue(value: string): void {
    this.ppCustomField().decimalPlaces = value;
  }

  setCurrency(newCurrency: TCurrencyData): void {
    this.currency = newCurrency.currencyCode;

    this.updateCustomField({
      ...this.ppCustomField(),
      currencyCode: newCurrency.currencyCode,
    });
  }

  toggleAddComma(): void {
    let showCommas;

    if (typeof this.ppCustomField().showCommas === 'undefined') {
      showCommas = false;
    } else {
      showCommas = !this.ppCustomField().showCommas;
    }

    this.updateCustomField({
      ...this.ppCustomField(),
      showCommas,
    });
  }

  toggleShowTotal(): void {
    let showTotal;

    if (this.ppCustomField().showTotal === undefined) {
      showTotal = false;
    } else {
      showTotal = !this.ppCustomField().showTotal;
    }

    this.updateCustomField({
      ...this.ppCustomField(),
      showTotal,
    });
  }

  toggleSubValuesActive(): void {
    let subValuesActive: boolean;

    subValuesActive = !this.ppCustomField().subValuesActive;

    this.updateCustomField({
      ...this.ppCustomField(),
      subValuesActive,
    });

    this.displaySubfieldWarning = !subValuesActive && this.initialCheckboxForSubfields;
  }

  toggleHoursOnly(): void {
    this.updateCustomField({
      ...this.ppCustomField(),
      showHoursOnly: !this.ppCustomField().showHoursOnly,
    });
  }

  increaseDecimal(): void {
    let decimalPlaces = this.ppCustomField().decimalPlaces || 0;

    if (+decimalPlaces < 4) {
      decimalPlaces = +decimalPlaces + 1;
    }

    this.updateCustomField({
      ...this.ppCustomField(),
      decimalPlaces,
    });
  }

  decreaseDecimal(): void {
    let decimalPlaces = +this.ppCustomField().decimalPlaces - 1;

    if (decimalPlaces < 0) {
      decimalPlaces = 0;
    }

    this.updateCustomField({
      ...this.ppCustomField(),
      decimalPlaces,
    });
  }

  checkListCF(): void {
    const slashFound = getIsContainsSlash(this.ppCustomField().subList);

    this.ppListError.emit(slashFound);
  }

  updateCustomField(field: TCustomField): void {
    this.ppCustomFieldChange.emit(field);
  }

  back(): void {
    this.ppGoBack.emit();
  }

  onFormulaChange(formula: string): void {
    this.updateCustomField({
      ...this.ppCustomField(),
      formula,
    });
  }

  private checkDepthExceeded(): void {
    this.maxListDepthExceeded = this.listDepth > this.maxListDepth;
  }

  private checkFeatureFlag(): void {
    this.customFieldGroupsEnabled = this.featureFlagsService.getFlag(
      EFeatureFlag.CUSTOM_FIELD_GROUPS,
    );

    this.featureFlagsService.flagsChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.customFieldGroupsEnabled = this.featureFlagsService.getFlag(
        EFeatureFlag.CUSTOM_FIELD_GROUPS,
      );
    });
  }
}
